import { useMemo, useState, useEffect } from 'react';
import { useFormacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { carregarHistoricoCategorias, getCategoriaDoHistorico, type HistoricoCategoria } from '@/utils/categoriaUtils';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface CategoriaRow {
  carreira: string;
  totalParticipacoes: number;
  totalFormacoes: number;
  totalHoras: number;
  custoTotal: number;
  porTipo: {
    tipo: string;
    participacoes: number;
    formacoes: number;
    horas: number;
    custo: number;
  }[];
}

interface FaixaRow {
  faixa: string;
  min: number;
  max: number;
  porCarreira: Record<string, number>;
  total: number;
}

interface CategoriaSocialRow {
  categoria: string;
  totalParticipacoes: number;
  totalFormacoes: number;
  totalHoras: number;
  custoTotal: number;
  porTipo: {
    tipo: string;
    participacoes: number;
    formacoes: number;
    horas: number;
    custo: number;
  }[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtHoras(h: number) {
  return h > 0 ? `${h}h` : '—';
}

function fmtEuro(v: number) {
  if (v === 0) return '0.00 €';
  return new Intl.NumberFormat('pt-PT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v) + ' €';
}

function fmtData(d: string | null) {
  if (!d) return 'Todos os períodos';
  return new Date(d).toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' });
}

const FAIXAS = [
  { faixa: '< 30h',       min: 0,   max: 29   },
  { faixa: '30 – 50h',    min: 30,  max: 50   },
  { faixa: '60 – 119h',   min: 60,  max: 119  },
  { faixa: '≥ 120h',      min: 120, max: 9999 },
];

const CARREIRAS_ORDEM = [
  'Técnica Superior',
  'Assistente Operacional',
  'Assistente Técnico',
  'Chefe Unidade',
  'Vereação',
];

// normaliza o tipo de modalidade para Interno/Externo
function normalizaTipo(modalidade: string | null): string {
  if (!modalidade) return 'Externo';
  const m = modalidade.toLowerCase();
  if (m.includes('intern')) return 'Interno';
  return 'Externo';
}

// soma horas das formações únicas (não multiplicadas por participantes)
function somaHorasFormacoesUnicas(
  formacaoIds: string[],
  formacoes: { id: string; duracao_horas?: number | null }[]
) {
  return [...new Set(formacaoIds)]
    .map((id) => formacoes.find((f) => f.id === id))
    .reduce((s, f) => s + (Number(f?.duracao_horas) || 0), 0);
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function BalancoSocialFormacao() {
  const { formacoes, participantes } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();

  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFim, setPeriodoFim] = useState('');
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set(CARREIRAS_ORDEM));
  const [historicoCategorias, setHistoricoCategorias] = useState<Record<string, HistoricoCategoria[]>>();

  // Carregar histórico de categorias
  useEffect(() => {
    const ids = funcionarios.map((f) => f.id);
    if (ids.length === 0) return;
    carregarHistoricoCategorias(ids).then(setHistoricoCategorias);
  }, [funcionarios]);

  // ── Filtragem por período ──────────────────────────────────────────────────
  const formacoesFiltradas = useMemo(() => {
    return formacoes.filter((f) => {
      const data = f.data_inicio ?? f.data_fim ?? '';
      if (periodoInicio && data < periodoInicio) return false;
      if (periodoFim && data > periodoFim) return false;
      return true;
    });
  }, [formacoes, periodoInicio, periodoFim]);

  const idsFormacoesFiltradas = useMemo(
    () => new Set(formacoesFiltradas.map((f) => f.id)),
    [formacoesFiltradas]
  );

  const participantesFiltrados = useMemo(
    () => participantes.filter((p) => idsFormacoesFiltradas.has(p.formacao_id)),
    [participantes, idsFormacoesFiltradas]
  );

  // Formações que têm pelo menos 1 participante no período — usado para totais consistentes
  const formacoesComParticipantes = useMemo(() => {
    const idsComPart = new Set(participantesFiltrados.map((p) => p.formacao_id));
    return formacoesFiltradas.filter((f) => idsComPart.has(f.id));
  }, [formacoesFiltradas, participantesFiltrados]);

  // Formações contadas por categoria (consistente com total geral da tabela)
  const totalFormacoesPorCategoria = useMemo(() => {
    const carreiraMap: Record<string, Set<string>> = {};
    participantesFiltrados.forEach((p) => {
      const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
      const formacao = formacoesComParticipantes.find((f) => f.id === p.formacao_id);
      if (!func || !formacao) return;
      const carreira = func.carreira ?? 'Outra';
      if (!carreiraMap[carreira]) carreiraMap[carreira] = new Set();
      carreiraMap[carreira].add(p.formacao_id);
    });
    return Object.values(carreiraMap).reduce((s, set) => s + set.size, 0);
  }, [participantesFiltrados, funcionarios, formacoesComParticipantes]);

  // ── Tabela por Categoria/Carreira e Tipo ───────────────────────────────────
  const tabelaCarreira = useMemo((): CategoriaRow[] => {
    const carreiraMap: Record<string, Record<string, { participacoes: number; formacoes: Set<string>; horasSet: Set<string>; custoSet: Set<string> }>> = {};

    participantesFiltrados.forEach((p) => {
      const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
      const formacao = formacoesComParticipantes.find((f) => f.id === p.formacao_id);
      if (!func || !formacao) return;

      const carreira = func.carreira ?? 'Outra';
      const tipo = normalizaTipo(formacao.modalidade);

      if (!carreiraMap[carreira]) carreiraMap[carreira] = {};
      if (!carreiraMap[carreira][tipo]) {
        carreiraMap[carreira][tipo] = { participacoes: 0, formacoes: new Set(), horasSet: new Set(), custoSet: new Set() };
      }

      carreiraMap[carreira][tipo].participacoes += 1;
      carreiraMap[carreira][tipo].formacoes.add(p.formacao_id);
      // guarda ID da formacao para somar horas/custo apenas uma vez
      carreiraMap[carreira][tipo].horasSet.add(p.formacao_id);
      carreiraMap[carreira][tipo].custoSet.add(p.formacao_id);
    });

    const rows: CategoriaRow[] = [];

    const todasCarreiras = Object.keys(carreiraMap);
    const ordenadas = [
      ...CARREIRAS_ORDEM.filter((c) => todasCarreiras.includes(c)),
      ...todasCarreiras.filter((c) => !CARREIRAS_ORDEM.includes(c)),
    ];

    ordenadas.forEach((carreira) => {
      const tiposMap = carreiraMap[carreira];
      const tiposOrdenados = Object.entries(tiposMap).sort((a, b) => a[0].localeCompare(b[0]));

      const totalParticipacoes = tiposOrdenados.reduce((s, [, v]) => s + v.participacoes, 0);
      const totalFormacoes = new Set(
        tiposOrdenados.flatMap(([, v]) => [...v.formacoes])
      ).size;

      // Horas: somar duracao das formações únicas com participantes desta carreira
      const idsFormacoesCarreira = participantesFiltrados
        .filter((p) => {
          const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
          return func?.carreira === carreira;
        })
        .map((p) => p.formacao_id);
      const totalHoras = somaHorasFormacoesUnicas(idsFormacoesCarreira, formacoesComParticipantes);

      // Custo: somar custo dos participantes desta carreira (ao nível do participante)
      const custoTotal = participantesFiltrados
        .filter((p) => {
          const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
          return func?.carreira === carreira;
        })
        .reduce((s, p) => s + Number(p.custo || 0), 0);

      rows.push({
        carreira,
        totalParticipacoes,
        totalFormacoes,
        totalHoras,
        custoTotal,
        porTipo: tiposOrdenados.map(([tipo, v]) => {
          // IDs das formações desta carreira e tipo
          const idsFormacoesTipo = participantesFiltrados
            .filter((p) => {
              const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
              const formacao = formacoesComParticipantes.find((f) => f.id === p.formacao_id);
              return func?.carreira === carreira && normalizaTipo(formacao?.modalidade) === tipo;
            })
            .map((p) => p.formacao_id);
          // Horas por tipo: somar duracao das formações únicas
          const horasTipo = somaHorasFormacoesUnicas(idsFormacoesTipo, formacoesComParticipantes);
          // Custo por tipo: somar custo dos participantes deste tipo
          const custoTipo = participantesFiltrados
            .filter((p) => {
              const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
              const formacao = formacoesComParticipantes.find((f) => f.id === p.formacao_id);
              return func?.carreira === carreira && normalizaTipo(formacao?.modalidade) === tipo;
            })
            .reduce((s, p) => s + Number(p.custo || 0), 0);
          return {
            tipo,
            participacoes: v.participacoes,
            formacoes: v.formacoes.size,
            horas: horasTipo,
            custo: custoTipo,
          };
        }),
      });
    });

    return rows;
  }, [formacoesComParticipantes, participantesFiltrados, funcionarios]);

  // Totais gerais da tabela — soma direta das linhas por categoria para consistência
  const totaisGerais = useMemo(() => {
    // Formações: somar total de formações por categoria
    const totalFormacoes = tabelaCarreira.reduce((s, row) => s + row.totalFormacoes, 0);
    // Horas: soma direta dos totais por categoria (consistente com a tabela)
    const totalHoras = tabelaCarreira.reduce((s, row) => s + row.totalHoras, 0);
    // Custo: somar custo dos participantes (definido ao nível de cada participante)
    const custoTotal = participantesFiltrados.reduce((s, p) => s + Number(p.custo || 0), 0);
    return {
      participacoes: participantesFiltrados.length,
      formacoes: totalFormacoes,
      totalHoras,
      custoTotal,
    };
  }, [tabelaCarreira, participantesFiltrados]);

  // ── KPIs globais ───────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const categoriasSet = new Set(formacoesComParticipantes.map((f) => f.categoria ?? 'Sem Categoria'));
    // Participações = total de inscrições (não deduplicado)
    const totalParticipacoes = participantesFiltrados.length;
    // Total de horas = soma direta dos totais por categoria (consistente com a tabela)
    const totalHoras = tabelaCarreira.reduce((s, row) => s + row.totalHoras, 0);
    // Custo: somar custo dos participantes
    const custoTotal = participantesFiltrados.reduce((s, p) => s + Number(p.custo || 0), 0);
    return {
      categorias: categoriasSet.size,
      participacoes: totalParticipacoes,
      formacoes: totalFormacoesPorCategoria,
      totalHoras,
      custoTotal,
    };
  }, [formacoesComParticipantes, participantesFiltrados, tabelaCarreira, totalFormacoesPorCategoria]);

  // ── Tabela por Faixa de Horas ──────────────────────────────────────────────
  const tabelaFaixas = useMemo((): FaixaRow[] => {
    const todasCarreiras = [...new Set(
      participantesFiltrados
        .map((p) => funcionarios.find((f) => f.id === p.funcionario_id)?.carreira)
        .filter(Boolean)
    )] as string[];

    return FAIXAS.map((fx) => {
      const porCarreira: Record<string, number> = {};
      let total = 0;

      todasCarreiras.forEach((carreira) => {
        // IDs das formações únicas desta carreira cuja duração cai na faixa
        const idsFormacoes = new Set(
          participantesFiltrados
            .filter((p) => {
              const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
              if (func?.carreira !== carreira) return false;
              const formacao = formacoesComParticipantes.find((f) => f.id === p.formacao_id);
              if (!formacao) return false;
              const h = Number(formacao.duracao_horas) || 0;
              return h >= fx.min && h <= fx.max;
            })
            .map((p) => p.formacao_id)
        );
        const count = idsFormacoes.size;
        porCarreira[carreira] = count;
        total += count;
      });

      return { ...fx, porCarreira, total };
    }).filter((r) => r.total > 0);
  }, [formacoesComParticipantes, participantesFiltrados, funcionarios]);

  const carreirasFaixa = useMemo(() => {
    return [...new Set(
      participantesFiltrados
        .map((p) => funcionarios.find((f) => f.id === p.funcionario_id)?.carreira)
        .filter(Boolean)
    )] as string[];
  }, [participantesFiltrados, funcionarios]);

  // ── Tabela por Modalidade/Tipo ─────────────────────────────────────────────
  const tabelaModalidade = useMemo(() => {
    const mapa: Record<string, { participacoes: number; formacoes: Set<string>; horas: number; custo: number }> = {};

    // Usar formacoesComParticipantes para garantir consistência
    formacoesComParticipantes.forEach((f) => {
      const tipo = f.modalidade?.trim() || 'Não especificado';
      if (!mapa[tipo]) mapa[tipo] = { participacoes: 0, formacoes: new Set(), horas: 0, custo: 0 };
      mapa[tipo].formacoes.add(f.id);
      mapa[tipo].horas += Number(f.duracao_horas) || 0;
      // Custo da formacao (ao nivel da formacao, nao do participante)
      mapa[tipo].custo += Number(f.custo || 0);
    });

    // Contar participacoes por modalidade
    participantesFiltrados.forEach((p) => {
      const formacao = formacoesComParticipantes.find((f) => f.id === p.formacao_id);
      if (!formacao) return;
      const tipo = formacao.modalidade?.trim() || 'Não especificado';
      if (!mapa[tipo]) return; // should not happen
      mapa[tipo].participacoes += 1;
      // Custo do participante
      mapa[tipo].custo += Number(p.custo || 0);
    });

    return Object.entries(mapa).map(([tipo, v]) => ({
      tipo,
      participacoes: v.participacoes,
      formacoes: v.formacoes.size,
      horas: v.horas,
      custo: v.custo,
    })).sort((a, b) => b.formacoes - a.formacoes);
  }, [formacoesComParticipantes, participantesFiltrados]);

  // Total geral da modalidade — soma das formações únicas por tipo (sem duplicação)
  const totaisModalidade = useMemo(() => {
    return {
      participacoes: tabelaModalidade.reduce((s, r) => s + r.participacoes, 0),
      formacoes: tabelaModalidade.reduce((s, r) => s + r.formacoes, 0),
      horas: tabelaModalidade.reduce((s, r) => s + r.horas, 0),
      custo: tabelaModalidade.reduce((s, r) => s + r.custo, 0),
    };
  }, [tabelaModalidade]);

  // ── Tabela por Categoria (respeita data de alteração) ────────────────────
  const tabelaCategoria = useMemo((): CategoriaSocialRow[] => {
    if (!historicoCategorias) return [];

    const categoriaMap: Record<string, Record<string, { participacoes: number; formacoes: Set<string>; horasSet: Set<string>; custoSet: Set<string> }>> = {};

    participantesFiltrados.forEach((p) => {
      const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
      const formacao = formacoesComParticipantes.find((f) => f.id === p.formacao_id);
      if (!func || !formacao) return;

      // Determinar a categoria do funcionário na data desta formação
      const dataRef = formacao.data_inicio || formacao.data_fim || '';
      const categoria = getCategoriaDoHistorico(
        func.id,
        dataRef,
        historicoCategorias,
        func.carreira
      );

      const tipo = normalizaTipo(formacao.modalidade);

      if (!categoriaMap[categoria]) categoriaMap[categoria] = {};
      if (!categoriaMap[categoria][tipo]) {
        categoriaMap[categoria][tipo] = { participacoes: 0, formacoes: new Set(), horasSet: new Set(), custoSet: new Set() };
      }

      categoriaMap[categoria][tipo].participacoes += 1;
      categoriaMap[categoria][tipo].formacoes.add(p.formacao_id);
      categoriaMap[categoria][tipo].horasSet.add(p.formacao_id);
      categoriaMap[categoria][tipo].custoSet.add(p.formacao_id);
    });

    const rows: CategoriaSocialRow[] = [];
    const todasCategorias = Object.keys(categoriaMap).sort();

    todasCategorias.forEach((categoria) => {
      const tiposMap = categoriaMap[categoria];
      const tiposOrdenados = Object.entries(tiposMap).sort((a, b) => a[0].localeCompare(b[0]));

      const totalParticipacoes = tiposOrdenados.reduce((s, [, v]) => s + v.participacoes, 0);
      const totalFormacoes = new Set(
        tiposOrdenados.flatMap(([, v]) => [...v.formacoes])
      ).size;

      const idsFormacoesCategoria = participantesFiltrados
        .filter((p) => {
          const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
          if (!func) return false;
          const formacao = formacoesComParticipantes.find((f) => f.id === p.formacao_id);
          if (!formacao) return false;
          const dataRef = formacao.data_inicio || formacao.data_fim || '';
          const cat = getCategoriaDoHistorico(func.id, dataRef, historicoCategorias, func.carreira);
          return cat === categoria;
        })
        .map((p) => p.formacao_id);
      const totalHoras = somaHorasFormacoesUnicas(idsFormacoesCategoria, formacoesComParticipantes);

      const custoTotal = participantesFiltrados
        .filter((p) => {
          const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
          if (!func) return false;
          const formacao = formacoesComParticipantes.find((f) => f.id === p.formacao_id);
          if (!formacao) return false;
          const dataRef = formacao.data_inicio || formacao.data_fim || '';
          const cat = getCategoriaDoHistorico(func.id, dataRef, historicoCategorias, func.carreira);
          return cat === categoria;
        })
        .reduce((s, p) => s + Number(p.custo || 0), 0);

      rows.push({
        categoria,
        totalParticipacoes,
        totalFormacoes,
        totalHoras,
        custoTotal,
        porTipo: tiposOrdenados.map(([tipo, v]) => {
          const idsFormacoesTipo = participantesFiltrados
            .filter((p) => {
              const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
              if (!func) return false;
              const formacao = formacoesComParticipantes.find((f) => f.id === p.formacao_id);
              if (!formacao) return false;
              const dataRef = formacao.data_inicio || formacao.data_fim || '';
              const cat = getCategoriaDoHistorico(func.id, dataRef, historicoCategorias, func.carreira);
              return cat === categoria && normalizaTipo(formacao.modalidade) === tipo;
            })
            .map((p) => p.formacao_id);
          const horasTipo = somaHorasFormacoesUnicas(idsFormacoesTipo, formacoesComParticipantes);
          const custoTipo = participantesFiltrados
            .filter((p) => {
              const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
              if (!func) return false;
              const formacao = formacoesComParticipantes.find((f) => f.id === p.formacao_id);
              if (!formacao) return false;
              const dataRef = formacao.data_inicio || formacao.data_fim || '';
              const cat = getCategoriaDoHistorico(func.id, dataRef, historicoCategorias, func.carreira);
              return cat === categoria && normalizaTipo(formacao.modalidade) === tipo;
            })
            .reduce((s, p) => s + Number(p.custo || 0), 0);
          return {
            tipo,
            participacoes: v.participacoes,
            formacoes: v.formacoes.size,
            horas: horasTipo,
            custo: custoTipo,
          };
        }),
      });
    });

    return rows;
  }, [participantesFiltrados, funcionarios, formacoesComParticipantes, historicoCategorias]);

  const totaisCategoria = useMemo(() => {
    return {
      participacoes: tabelaCategoria.reduce((s, r) => s + r.totalParticipacoes, 0),
      formacoes: tabelaCategoria.reduce((s, r) => s + r.totalFormacoes, 0),
      totalHoras: tabelaCategoria.reduce((s, r) => s + r.totalHoras, 0),
      custoTotal: tabelaCategoria.reduce((s, r) => s + r.custoTotal, 0),
    };
  }, [tabelaCategoria]);

  // ── Toggle expandir linhas ─────────────────────────────────────────────────
  function toggleCarreira(carreira: string) {
    setExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(carreira)) next.delete(carreira);
      else next.add(carreira);
      return next;
    });
  }

  const [expandidasCategoria, setExpandidasCategoria] = useState<Set<string>>(new Set());

  function toggleCategoria(categoria: string) {
    setExpandidasCategoria((prev) => {
      const next = new Set(prev);
      if (next.has(categoria)) next.delete(categoria);
      else next.add(categoria);
      return next;
    });
  }

  function expandirTodas() {
    setExpandidas(new Set(tabelaCarreira.map((r) => r.carreira)));
  }

  function recolherTodas() {
    setExpandidas(new Set());
  }

  function expandirTodasCategorias() {
    setExpandidasCategoria(new Set(tabelaCategoria.map((r) => r.categoria)));
  }

  function recolherTodasCategorias() {
    setExpandidasCategoria(new Set());
  }

  // ── Período label ──────────────────────────────────────────────────────────
  const periodoLabel = useMemo(() => {
    if (!periodoInicio && !periodoFim) return 'Todos os períodos';
    if (periodoInicio && periodoFim) return `${fmtData(periodoInicio)} — ${fmtData(periodoFim)}`;
    if (periodoInicio) return `A partir de ${fmtData(periodoInicio)}`;
    return `Até ${fmtData(periodoFim)}`;
  }, [periodoInicio, periodoFim]);

  // ── Exportar PDF ───────────────────────────────────────────────────────────
  function exportarPDF() {
    const dataGeracao = new Date().toLocaleDateString('pt-PT', {
      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

    const carrihasHtml = tabelaCarreira.map((row) => {
      const tiposHtml = expandidas.has(row.carreira)
        ? row.porTipo.map((t) => `
          <tr class="sub-row">
            <td></td>
            <td><span class="tipo-badge">${t.tipo}</span></td>
            <td class="num badge-formandos">${t.participacoes}</td>
            <td class="num badge-formacoes">${t.formacoes}</td>
            <td class="num badge-horas">${fmtHoras(t.horas)}</td>
            <td class="num">${fmtEuro(t.custo)}</td>
          </tr>`).join('')
        : '';
      return `
        <tr class="cat-row">
          <td class="cat-name">${row.carreira}</td>
          <td class="tipo-label">Total</td>
          <td class="num badge-formandos">${row.totalParticipacoes}</td>
          <td class="num badge-formacoes">${row.totalFormacoes}</td>
          <td class="num badge-horas">${fmtHoras(row.totalHoras)}</td>
          <td class="num">${fmtEuro(row.custoTotal)}</td>
        </tr>
        ${tiposHtml}`;
    }).join('');

    const faixasHtml = tabelaFaixas.map((row) => `
      <tr>
        <td class="font-bold">${row.faixa}</td>
        ${carreirasFaixa.map((c) => `<td class="num">${row.porCarreira[c] ?? 0}</td>`).join('')}
        <td class="num badge-formandos">${row.total}</td>
      </tr>`).join('');

    const modalidadeHtml = tabelaModalidade.map((row) => `
      <tr>
        <td class="font-bold">${row.tipo}</td>
        <td class="num">${row.participacoes}</td>
        <td class="num">${row.formacoes}</td>
        <td class="num">${fmtHoras(row.horas)}</td>
        <td class="num">${fmtEuro(row.custo)}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="pt">
<head>
<meta charset="utf-8">
<title>Balanço Social — Formação Profissional</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; }
  .page { max-width: 960px; margin: 0 auto; padding: 28px 36px; }
  .header-box { background: linear-gradient(135deg, #1a5c4a 0%, #2d8a6c 100%); color: #fff; border-radius: 8px; padding: 20px 24px; margin-bottom: 20px; }
  .header-box h1 { font-size: 20px; font-weight: 800; letter-spacing: -0.3px; }
  .header-box p { font-size: 11px; opacity: 0.8; margin-top: 4px; }
  .meta-bar { display: flex; justify-content: flex-end; font-size: 9px; color: #888; margin-bottom: 16px; }
  .kpis { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-bottom: 22px; }
  .kpi { border: 1.5px solid #e2e8f0; border-radius: 6px; padding: 12px 8px; text-align: center; }
  .kpi-val { font-size: 22px; font-weight: 900; color: #111; }
  .kpi-lbl { font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #888; margin-top: 3px; }
  .section { margin-bottom: 24px; }
  .section-title { font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; color: #333; margin-bottom: 10px; padding-left: 8px; border-left: 3px solid #1a5c4a; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #f1f5f9; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #64748b; padding: 7px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; }
  th.num { text-align: center; }
  td { font-size: 10px; padding: 6px 10px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
  td.num { text-align: center; }
  .cat-row { background: #fafafa; }
  .cat-row .cat-name { font-weight: 800; font-size: 11px; color: #1a1a1a; }
  .cat-row .tipo-label { font-size: 10px; font-weight: 600; color: #475569; }
  .sub-row td { background: #fff; padding-left: 24px; }
  .tipo-badge { display: inline-block; font-size: 9px; font-weight: 700; padding: 2px 8px; border-radius: 10px; background: #f1f5f9; color: #475569; }
  .badge-formandos { color: #7c3aed; }
  .badge-formacoes { color: #0891b2; font-weight: 700; }
  .badge-horas { color: #d97706; font-weight: 700; }
  .total-row { background: #1a5c4a !important; }
  .total-row td { color: #fff; font-weight: 800; font-size: 11px; padding: 9px 10px; }
  .total-badge { display: inline-block; background: rgba(255,255,255,0.25); border-radius: 10px; padding: 2px 10px; }
  .font-bold { font-weight: 700; }
  .footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 9px; color: #aaa; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>
<div class="page">
  <div class="header-box">
    <h1>Balanço Social – Formação Profissional</h1>
    <p>Período: ${periodoLabel} · Gerado em ${dataGeracao}</p>
  </div>

  <div class="kpis">
    <div class="kpi"><div class="kpi-val">${kpis.categorias}</div><div class="kpi-lbl">Categorias</div></div>
    <div class="kpi"><div class="kpi-val">${kpis.participacoes}</div><div class="kpi-lbl">Participações</div></div>
    <div class="kpi"><div class="kpi-val">${kpis.formacoes}</div><div class="kpi-lbl">Formações</div></div>
    <div class="kpi"><div class="kpi-val">${kpis.totalHoras}h</div><div class="kpi-lbl">Total Horas</div></div>
    <div class="kpi"><div class="kpi-val">${fmtEuro(kpis.custoTotal)}</div><div class="kpi-lbl">Custo Total</div></div>
  </div>

  <div class="section">
    <div class="section-title">Horas de Formação por Categoria e Tipo</div>
    <table>
      <thead>
        <tr>
          <th>Categoria</th>
          <th>Tipo de Formação</th>
          <th class="num">Nº Participações</th>
          <th class="num">Nº Formações</th>
          <th class="num">Total Horas</th>
          <th class="num">Custo Total</th>
        </tr>
      </thead>
      <tbody>
        ${carrihasHtml}
        <tr class="total-row">
          <td colspan="2">TOTAL GERAL</td>
          <td class="num"><span class="total-badge">${totaisGerais.participacoes} participações</span></td>
          <td class="num"><span class="total-badge">${totaisGerais.formacoes} formações</span></td>
          <td class="num"><span class="total-badge">${fmtHoras(totaisGerais.totalHoras)}</span></td>
          <td class="num"><span class="total-badge">${fmtEuro(totaisGerais.custoTotal)}</span></td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="section">
    <div class="section-title">Ações de Formação por Faixa de Horas e Categoria</div>
    <table>
      <thead>
        <tr>
          <th>Faixa de Horas</th>
          ${carreirasFaixa.map((c) => `<th class="num">${c}</th>`).join('')}
          <th class="num">Total</th>
        </tr>
      </thead>
      <tbody>
        ${faixasHtml}
        <tr style="background:#f8fafc; font-weight:800;">
          <td>TOTAL</td>
          ${carreirasFaixa.map((c) => `<td class="num">${tabelaFaixas.reduce((s, r) => s + (r.porCarreira[c] ?? 0), 0)}</td>`).join('')}
          <td class="num badge-formandos">${tabelaFaixas.reduce((s, r) => s + r.total, 0)}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="section">
    <div class="section-title">Formação por Modalidade</div>
    <table>
      <thead>
        <tr>
          <th>Modalidade</th>
          <th class="num">Participações</th>
          <th class="num">Formações</th>
          <th class="num">Horas</th>
          <th class="num">Custo Total</th>
        </tr>
      </thead>
      <tbody>
        ${modalidadeHtml}
        <tr style="background:#f8fafc; font-weight:800;">
          <td>TOTAL</td>
          <td class="num badge-formandos">${totaisGerais.participacoes}</td>
          <td class="num badge-formacoes">${totaisGerais.formacoes}</td>
          <td class="num badge-horas">${fmtHoras(totaisGerais.totalHoras)}</td>
          <td class="num">${fmtEuro(totaisGerais.custoTotal)}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="footer">
    <div>Gestão da Formação Municipal — Balanço Social Confidencial</div>
    <div>Gerado automaticamente em ${dataGeracao}</div>
  </div>
</div>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 800);
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">

      {/* Filtros de período + acções */}
      <div className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
            <i className="ri-calendar-line text-sm" />
          </div>
          <span className="text-[12px] font-semibold text-zinc-600">Período</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={periodoInicio}
            onChange={(e) => setPeriodoInicio(e.target.value)}
            className="text-[12px] px-3 py-2 border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-zinc-300 text-zinc-700"
          />
          <span className="text-[12px] text-zinc-400">até</span>
          <input
            type="date"
            value={periodoFim}
            onChange={(e) => setPeriodoFim(e.target.value)}
            className="text-[12px] px-3 py-2 border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-zinc-300 text-zinc-700"
          />
          {(periodoInicio || periodoFim) && (
            <button
              onClick={() => { setPeriodoInicio(''); setPeriodoFim(''); }}
              className="whitespace-nowrap text-[11px] text-zinc-400 hover:text-zinc-700 px-2 py-1 rounded-md hover:bg-zinc-100 transition-colors cursor-pointer"
            >
              Limpar
            </button>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={expandirTodas}
            className="whitespace-nowrap text-[12px] font-medium text-zinc-500 hover:text-zinc-800 px-3 py-2 rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            Expandir carreiras
          </button>
          <button
            onClick={recolherTodas}
            className="whitespace-nowrap text-[12px] font-medium text-zinc-500 hover:text-zinc-800 px-3 py-2 rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            Recolher carreiras
          </button>
          <button
            onClick={expandirTodasCategorias}
            className="whitespace-nowrap text-[12px] font-medium text-zinc-500 hover:text-zinc-800 px-3 py-2 rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            Expandir categorias
          </button>
          <button
            onClick={recolherTodasCategorias}
            className="whitespace-nowrap text-[12px] font-medium text-zinc-500 hover:text-zinc-800 px-3 py-2 rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            Recolher categorias
          </button>
          <button
            onClick={exportarPDF}
            className="whitespace-nowrap flex items-center gap-2 bg-emerald-700 text-white text-[12px] font-semibold px-4 py-2 rounded-lg hover:bg-emerald-800 transition-colors cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-file-pdf-line text-sm" />
            </div>
            Exportar PDF
          </button>
        </div>
      </div>

      {/* Cabeçalho estilo documento */}
      <div className="bg-gradient-to-r from-emerald-800 to-teal-700 rounded-2xl p-6 text-white">
        <h2 className="text-[20px] font-black tracking-tight">Balanço Social – Formação Profissional</h2>
        <p className="text-[12px] text-white/70 mt-1">Período: {periodoLabel}</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { value: String(kpis.categorias), label: 'CATEGORIAS' },
          { value: String(kpis.participacoes), label: 'PARTICIPAÇÕES' },
          { value: String(kpis.formacoes), label: 'FORMAÇÕES' },
          { value: `${kpis.totalHoras}h`, label: 'TOTAL HORAS' },
          { value: fmtEuro(kpis.custoTotal), label: 'CUSTO TOTAL' },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-xl border border-zinc-100 px-4 py-4 text-center">
            <p className="text-[26px] font-black text-zinc-900 leading-none">{kpi.value}</p>
            <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mt-2">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* ── TABELA 1: Por Categoria (Carreira) e Tipo ── */}
      <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1 h-5 bg-emerald-700 rounded-full" />
            <h3 className="text-[14px] font-bold text-zinc-800">Horas de Formação por Categoria e Tipo</h3>
          </div>
          <p className="text-[11px] text-zinc-400">Clique numa categoria para ver o detalhe por tipo</p>
        </div>

        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="px-6 py-3 text-left text-[10px] font-bold text-zinc-400 uppercase tracking-wider w-[220px]">Categoria</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Tipo de Formação</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Nº Participações</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Nº Formações</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Total Horas</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Custo Total</th>
            </tr>
          </thead>
          <tbody>
            {tabelaCarreira.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-[13px] text-zinc-400">
                  Sem dados para o período selecionado
                </td>
              </tr>
            ) : (
              tabelaCarreira.map((row) => (
                <>
                  {/* Linha categoria */}
                  <tr
                    key={`cat-${row.carreira}`}
                    onClick={() => toggleCarreira(row.carreira)}
                    className="border-b border-zinc-100 bg-zinc-50/40 hover:bg-zinc-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center text-zinc-400 flex-shrink-0">
                          {expandidas.has(row.carreira)
                            ? <i className="ri-arrow-down-s-line text-sm" />
                            : <i className="ri-arrow-right-s-line text-sm" />}
                        </div>
                        <span className="text-[13px] font-bold text-zinc-800">{row.carreira}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-[12px] font-semibold text-zinc-500">Total</span>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-violet-100 text-violet-700 text-[12px] font-bold">
                        {row.totalParticipacoes}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-sky-100 text-sky-700 text-[12px] font-bold">
                        {row.totalFormacoes}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-flex items-center justify-center min-w-[40px] h-7 px-2.5 rounded-full bg-amber-100 text-amber-700 text-[12px] font-bold">
                        {fmtHoras(row.totalHoras)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <span className="text-[12px] font-semibold text-zinc-700">{fmtEuro(row.custoTotal)}</span>
                    </td>
                  </tr>

                  {/* Sub-linhas por tipo */}
                  {expandidas.has(row.carreira) && row.porTipo.map((t) => (
                    <tr key={`${row.carreira}-${t.tipo}`} className="border-b border-zinc-50 bg-white">
                      <td className="pl-12 pr-4 py-2.5">
                        <div className="w-5 h-5 flex items-center justify-center text-zinc-300 flex-shrink-0">
                          <i className="ri-corner-down-right-line text-xs" />
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-600">
                          {t.tipo === 'Externo' ? (
                            <i className="ri-building-2-line text-[10px]" />
                          ) : (
                            <i className="ri-home-4-line text-[10px]" />
                          )}
                          {t.tipo}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] h-6 px-2 rounded-full bg-violet-50 text-violet-600 text-[11px] font-bold">
                          {t.participacoes}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] h-6 px-2 rounded-full bg-sky-50 text-sky-600 text-[11px] font-bold">
                          {t.formacoes}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className="inline-flex items-center justify-center min-w-[40px] h-6 px-2 rounded-full bg-amber-50 text-amber-600 text-[11px] font-bold">
                          {fmtHoras(t.horas)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className="text-[11px] text-zinc-600">{fmtEuro(t.custo)}</span>
                      </td>
                    </tr>
                  ))}
                </>
              ))
            )}

            {/* TOTAL GERAL */}
            {tabelaCarreira.length > 0 && (
              <tr className="bg-emerald-800 border-t-2 border-emerald-900">
                <td colSpan={2} className="px-6 py-4">
                  <span className="text-[13px] font-black text-white tracking-wide">TOTAL GERAL</span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex items-center justify-center min-w-[60px] h-7 px-3 rounded-full bg-white/20 text-white text-[12px] font-black">
                    {totaisGerais.participacoes} participações
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex items-center justify-center min-w-[60px] h-7 px-3 rounded-full bg-white/20 text-white text-[12px] font-black">
                    {totaisGerais.formacoes} formações
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex items-center justify-center min-w-[50px] h-7 px-3 rounded-full bg-white/20 text-white text-[12px] font-black">
                    {fmtHoras(totaisGerais.totalHoras)}
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="text-[13px] font-black text-white">{fmtEuro(totaisGerais.custoTotal)}</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── TABELA 1b: Por Categoria (respeita datas de alteração) ── */}
      <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1 h-5 bg-teal-600 rounded-full" />
            <h3 className="text-[14px] font-bold text-zinc-800">Horas de Formação por Categoria e Tipo (histórico)</h3>
          </div>
          <p className="text-[11px] text-zinc-400">Respeita alterações de categoria com data</p>
        </div>

        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="px-6 py-3 text-left text-[10px] font-bold text-zinc-400 uppercase tracking-wider w-[220px]">Categoria</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Tipo de Formação</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Nº Participações</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Nº Formações</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Total Horas</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Custo Total</th>
            </tr>
          </thead>
          <tbody>
            {tabelaCategoria.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-[13px] text-zinc-400">
                  {!historicoCategorias ? 'A carregar histórico de categorias...' : 'Sem dados para o período selecionado'}
                </td>
              </tr>
            ) : (
              tabelaCategoria.map((row) => (
                <>
                  {/* Linha categoria */}
                  <tr
                    key={`cat-${row.categoria}`}
                    onClick={() => toggleCategoria(row.categoria)}
                    className="border-b border-zinc-100 bg-zinc-50/40 hover:bg-zinc-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center text-zinc-400 flex-shrink-0">
                          {expandidasCategoria.has(row.categoria)
                            ? <i className="ri-arrow-down-s-line text-sm" />
                            : <i className="ri-arrow-right-s-line text-sm" />}
                        </div>
                        <span className="text-[13px] font-bold text-zinc-800">{row.categoria}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-[12px] font-semibold text-zinc-500">Total</span>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-violet-100 text-violet-700 text-[12px] font-bold">
                        {row.totalParticipacoes}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-sky-100 text-sky-700 text-[12px] font-bold">
                        {row.totalFormacoes}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-flex items-center justify-center min-w-[40px] h-7 px-2.5 rounded-full bg-amber-100 text-amber-700 text-[12px] font-bold">
                        {fmtHoras(row.totalHoras)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <span className="text-[12px] font-semibold text-zinc-700">{fmtEuro(row.custoTotal)}</span>
                    </td>
                  </tr>

                  {/* Sub-linhas por tipo */}
                  {expandidasCategoria.has(row.categoria) && row.porTipo.map((t) => (
                    <tr key={`${row.categoria}-${t.tipo}`} className="border-b border-zinc-50 bg-white">
                      <td className="pl-12 pr-4 py-2.5">
                        <div className="w-5 h-5 flex items-center justify-center text-zinc-300 flex-shrink-0">
                          <i className="ri-corner-down-right-line text-xs" />
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-600">
                          {t.tipo === 'Externo' ? (
                            <i className="ri-building-2-line text-[10px]" />
                          ) : (
                            <i className="ri-home-4-line text-[10px]" />
                          )}
                          {t.tipo}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] h-6 px-2 rounded-full bg-violet-50 text-violet-600 text-[11px] font-bold">
                          {t.participacoes}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] h-6 px-2 rounded-full bg-sky-50 text-sky-600 text-[11px] font-bold">
                          {t.formacoes}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className="inline-flex items-center justify-center min-w-[40px] h-6 px-2 rounded-full bg-amber-50 text-amber-600 text-[11px] font-bold">
                          {fmtHoras(t.horas)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className="text-[11px] text-zinc-600">{fmtEuro(t.custo)}</span>
                      </td>
                    </tr>
                  ))}
                </>
              ))
            )}

            {/* TOTAL GERAL */}
            {tabelaCategoria.length > 0 && (
              <tr className="bg-teal-700 border-t-2 border-teal-800">
                <td colSpan={2} className="px-6 py-4">
                  <span className="text-[13px] font-black text-white tracking-wide">TOTAL GERAL</span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex items-center justify-center min-w-[60px] h-7 px-3 rounded-full bg-white/20 text-white text-[12px] font-black">
                    {totaisCategoria.participacoes} participações
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex items-center justify-center min-w-[60px] h-7 px-3 rounded-full bg-white/20 text-white text-[12px] font-black">
                    {totaisCategoria.formacoes} formações
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex items-center justify-center min-w-[50px] h-7 px-3 rounded-full bg-white/20 text-white text-[12px] font-black">
                    {fmtHoras(totaisCategoria.totalHoras)}
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="text-[13px] font-black text-white">{fmtEuro(totaisCategoria.custoTotal)}</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── TABELA 2: Ações de Formação por Faixa de Horas ── */}
      <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100">
          <div className="flex items-center gap-2">
            <div className="w-1 h-5 bg-emerald-700 rounded-full" />
            <h3 className="text-[14px] font-bold text-zinc-800">Ações de Formação por Faixa de Horas e Categoria</h3>
          </div>
          <p className="text-[11px] text-zinc-400 mt-1 ml-3">Número de ações de formação por faixa de duração e por carreira</p>
        </div>

        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="px-6 py-3 text-left text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Faixa de Horas</th>
              {carreirasFaixa.map((c) => (
                <th key={c} className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">{c}</th>
              ))}
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Total</th>
            </tr>
          </thead>
          <tbody>
            {tabelaFaixas.length === 0 ? (
              <tr>
                <td colSpan={carreirasFaixa.length + 2} className="px-6 py-12 text-center text-[13px] text-zinc-400">
                  Sem dados para o período selecionado
                </td>
              </tr>
            ) : (
              <>
                {tabelaFaixas.map((row, i) => (
                  <tr key={row.faixa} className={`border-b border-zinc-50 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/30'}`}>
                    <td className="px-6 py-3.5">
                      <span className="text-[13px] font-bold text-zinc-800">{row.faixa}</span>
                    </td>
                    {carreirasFaixa.map((c) => (
                      <td key={c} className="px-4 py-3.5 text-center">
                        {(row.porCarreira[c] ?? 0) > 0 ? (
                          <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-emerald-50 text-emerald-700 text-[12px] font-bold">
                            {row.porCarreira[c]}
                          </span>
                        ) : (
                          <span className="text-[12px] text-zinc-300">—</span>
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-violet-100 text-violet-700 text-[13px] font-black">
                        {row.total}
                      </span>
                    </td>
                  </tr>
                ))}
                {/* Total linha */}
                <tr className="bg-zinc-50 border-t-2 border-zinc-200">
                  <td className="px-6 py-3.5">
                    <span className="text-[12px] font-black text-zinc-700 uppercase tracking-wide">Total</span>
                  </td>
                  {carreirasFaixa.map((c) => (
                    <td key={c} className="px-4 py-3.5 text-center">
                      <span className="text-[12px] font-bold text-zinc-700">
                        {tabelaFaixas.reduce((s, r) => s + (r.porCarreira[c] ?? 0), 0)}
                      </span>
                    </td>
                  ))}
                  <td className="px-4 py-3.5 text-center">
                    <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-violet-100 text-violet-700 text-[13px] font-black">
                      {tabelaFaixas.reduce((s, r) => s + r.total, 0)}
                    </span>
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* ── TABELA 3: Por Modalidade ── */}
      <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100">
          <div className="flex items-center gap-2">
            <div className="w-1 h-5 bg-emerald-700 rounded-full" />
            <h3 className="text-[14px] font-bold text-zinc-800">Formação por Modalidade</h3>
          </div>
          <p className="text-[11px] text-zinc-400 mt-1 ml-3">Distribuição entre formação interna e externa</p>
        </div>

        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="px-6 py-3 text-left text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Modalidade</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Participações</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Formações</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Total Horas</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Custo Total</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">% Participações</th>
            </tr>
          </thead>
          <tbody>
            {tabelaModalidade.map((row, i) => {
              const pct = totaisModalidade.participacoes > 0
                ? Math.round((row.participacoes / totaisModalidade.participacoes) * 100)
                : 0;
              return (
                <tr key={row.tipo} className={`border-b border-zinc-50 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/30'}`}>
                  <td className="px-6 py-3.5">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${
                        row.tipo === 'Presencial' ? 'bg-emerald-50' : row.tipo === 'E-Learning' ? 'bg-sky-50' : 'bg-zinc-100'
                      }`}>
                        <i className={`${
                          row.tipo === 'Presencial' ? 'ri-building-2-line text-emerald-600' : 
                          row.tipo === 'E-Learning' ? 'ri-computer-line text-sky-600' : 
                          'ri-question-line text-zinc-500'
                        } text-sm`} />
                      </div>
                      <span className="text-[13px] font-bold text-zinc-800">{row.tipo}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-violet-100 text-violet-700 text-[12px] font-bold">
                      {row.participacoes}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-sky-100 text-sky-700 text-[12px] font-bold">
                      {row.formacoes}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <span className="inline-flex items-center justify-center min-w-[40px] h-7 px-2.5 rounded-full bg-amber-100 text-amber-700 text-[12px] font-bold">
                      {fmtHoras(row.horas)}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <span className="text-[12px] font-semibold text-zinc-700">{fmtEuro(row.custo)}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2 justify-center">
                      <div className="flex-1 max-w-[80px] h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            row.tipo === 'Presencial' ? 'bg-emerald-500' : row.tipo === 'E-Learning' ? 'bg-sky-500' : 'bg-zinc-400'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-[11px] font-bold text-zinc-600 w-8 text-right flex-shrink-0">{pct}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {/* Total */}
            <tr className="bg-zinc-50 border-t-2 border-zinc-200">
              <td className="px-6 py-3.5">
                <span className="text-[12px] font-black text-zinc-700 uppercase tracking-wide">Total</span>
              </td>
              <td className="px-4 py-3.5 text-center">
                <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-violet-100 text-violet-700 text-[12px] font-black">
                  {totaisModalidade.participacoes}
                </span>
              </td>
              <td className="px-4 py-3.5 text-center">
                <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-sky-100 text-sky-700 text-[12px] font-black">
                  {totaisModalidade.formacoes}
                </span>
              </td>
              <td className="px-4 py-3.5 text-center">
                <span className="inline-flex items-center justify-center min-w-[40px] h-7 px-2.5 rounded-full bg-amber-100 text-amber-700 text-[12px] font-black">
                  {fmtHoras(totaisModalidade.horas)}
                </span>
              </td>
              <td className="px-4 py-3.5 text-center">
                <span className="text-[12px] font-black text-zinc-800">{fmtEuro(totaisModalidade.custo)}</span>
              </td>
              <td className="px-4 py-3.5 text-center">
                <span className="text-[12px] font-black text-zinc-600">100%</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}